import { PlaySymbolSoundCommand } from "slotworks/commands/symbols/play-symbol-sound-command";
import Components from "slotworks/components/components";
import Layers from "slotworks/graphics/layers/layers";
import Scene from "slotworks/graphics/layers/scene";
import betModel from "slotworks/model/bets/bet-model";
import LineResult from "slotworks/model/gameplay/records/results/line-result";
import Result from "slotworks/model/gameplay/records/results/result";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import WaysResult from "slotworks/model/gameplay/records/results/ways-result";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import * as TweenJS from "slotworks/utils/tween";

export default class CelebrateWinComponent {

    public static TOTAL_COUNTUP_TIME: number = 750;
    public static TOTAL_WAIT_TIME: number = 750;
    public static LINE_COUNTUP_TIME: number = 0;
    public static LINE_WAIT_TIME: number = 1250;
    public static ENABLE_TICKUP: boolean = true;
    public static ENABLE_PERSISTENT_TOTAL_WIN: boolean = false;
    public static TOTAL_WIN_THRESHOLD: number = 0;
    public WIN_RANGES: Map<string, { from: number, to: number }> = new Map<string, { from: number, to: number }>([
        ["LOW", { from: 0, to: 9 }],
        ["MED", { from: 10, to: 19 }]
    ] as any);

    private tween: TweenJS.Tween;
    private currentWins: Win[] = [];

    public totalWin(winAmount: number) {

        const win = new Win(winAmount, ["totalWin"]);

        if (!win.valid) {
            return Contract.empty();
        }

        return new Sequence([
            () => this.celebrateWin(win),
            () => this.completeTotalWin()
        ]);
    }

    public singleTotalWin(winAmount: number, result: SymbolWinResult) {
        const win = new Win(winAmount, ["totalWin"], result);

        if (!win.valid) {
            return Contract.empty();
        }

        return new Sequence([
            () => this.celebrateWin(win),
            () => this.completeTotalWin()
        ]);
    }

    public additionalWin(winAmount: number) {
        const win = new Win(winAmount, ["additionalWin", "totalWin"]);
        return new Sequence([
            () => this.celebrateWin(win),
            () => Contract.getTimeoutContract(CelebrateWinComponent.TOTAL_WAIT_TIME),
            () => Layers.CelebrationContent.defaultScene()
        ]);
    }

    public lineWin(result: LineResult, symbol?: string, row?: number) {

        const scenes = [
            `line_${result.line}_${result.matches}_win`,
            `line_${result.line}_win`,
            `line_row_${row}_win`,
            "line_win",
            "lineWin"
        ];

        const win = new Win(result.cashWon, scenes, result);

        if (!win.valid) {
            return Contract.empty();
        }

        this.cleanup();

        if (!win.canTickUp()) {
            Components.footer.showLineWin(result, symbol);
            return Contract.empty();
        } else {
            const setup = () => {
                new Parallel([
                    () => Contract.wrap(() => {
                        Services.sound.event(SoundEvent.tickup_linewin_start);
                        Components.footer.showLineWin(result, symbol);
                    }),
                    () => this.showLineWin(win.winAmount)
                ]).execute();
            };

            const totalTime = this.getLineWinCountupDuration();

            const sequence = new Sequence<void>([
                () => Layers.CelebrationContent.setScene(win.scene, setup),
                () => Contract.getTimeoutContract(totalTime),
                () => Layers.CelebrationContent.defaultScene()
            ]);

            win.contract = sequence;
            this.currentWins.push(win);

            return sequence;
        }
    }

    public miscWin(result: Result, textId?: string) {
        return Contract.wrap(() => {
            this.cleanup();
            Components.footer.showMiscWin(textId);
        });
    }

    public waysWin(result: WaysResult, symbol?: string) {

        const win = new Win(result.cashWon, [], result);

        if (win.valid) {
            Services.sound.event(SoundEvent.wayswin_start);
            Components.footer.showWaysWin(result, symbol);
        }
        return Contract.empty();
    }

    public cleanup(): void {
        if (this.tween) {
            Services.sound.event(SoundEvent.tickup_anywin_end);
            this.tween.stop();
        }

        while (this.currentWins.length > 0) {
            const win = this.currentWins.shift();
            win.contract.cancel();
            win.destroy();
        }

        if (!CelebrateWinComponent.ENABLE_PERSISTENT_TOTAL_WIN) {
            this.hide();
        }
    }

    public hide(): void {
        Layers.CelebrationContent.jumpToScene("default");
        Components.footer.showDefaultText();
    }

    private celebrateWin(win: Win) {

        this.cleanup();

        Services.sound.event(SoundEvent.totalwin_start);

        if (!win.canTickUp()) {
            Components.footer.showGameWin(win.winAmount);
            return Contract.empty();
        } else {
            const setup = () => {
                new Parallel([
                    () => Contract.wrap(() => {
                        win.playSound();
                        Components.footer.showGameWin(win.winAmount);
                    }),
                    () => this.tickupTotalWin(win.winAmount)
                ]).execute();
            };

            const totalTime = this.getTotalWinCountupDuration();

            const sequence = new Sequence<void>([
                () => Layers.CelebrationContent.setScene(win.scene, setup),
                () => Contract.getTimeoutContract(totalTime)
            ]);

            win.contract = sequence;
            this.currentWins.push(win);

            return sequence;
        }
    }

    private instantUpdateText(text: PIXI.Text, targetValue: number, wait: number, completeSound?: SoundEvent) {
        Services.sound.event(SoundEvent.tickup_anywin_start);
        Services.sound.event(SoundEvent.tickup_anywin_end);

        const winRangeKey: string = this.getWinRangeForWin(targetValue);
        if (winRangeKey !== "") {
            Services.sound.event(SoundEvent.tickup_win_range, winRangeKey);
        }

        text.text = Services.currency.format(targetValue);

        if (completeSound) {
            Services.sound.event(completeSound);
        }

        return Contract.getTimeoutContract(wait);
    }

    private tickupTotalWin(amountWon: number) {
        return this.tickUp(
            Layers.CelebrationContent.getText("value"),
            amountWon,
            CelebrateWinComponent.TOTAL_COUNTUP_TIME,
            CelebrateWinComponent.TOTAL_WAIT_TIME,
            SoundEvent.tickup_totalwin_end
        );
    }

    private showLineWin(amountWon: number) {
        if (CelebrateWinComponent.LINE_COUNTUP_TIME === Infinity || CelebrateWinComponent.LINE_COUNTUP_TIME === 0) {
            return this.instantUpdateText(Layers.CelebrationContent.getText("value"), amountWon, CelebrateWinComponent.LINE_WAIT_TIME);
        } else {
            return this.tickUp(Layers.CelebrationContent.getText("value"), amountWon, CelebrateWinComponent.LINE_COUNTUP_TIME, CelebrateWinComponent.LINE_WAIT_TIME);
        }
    }

    private tickUp(text: PIXI.Text, targetValue: number, duration: number, wait: number, completeSound?: SoundEvent) {
        return new Sequence([
            () => this.tickTween(text, targetValue, duration, completeSound),
            () => Contract.getTimeoutContract(wait)
        ]);
    }

    private tickTween(text: PIXI.Text, targetValue: number, duration: number, completeSound?: SoundEvent) {
        return new Contract<void>((resolve) => {
            Services.sound.event(SoundEvent.tickup_anywin_start);

            const tickObj = {
                value: 0
            };
            this.tween = new TweenJS.Tween(tickObj)
                .to({ value: targetValue }, duration)
                .onStart(() => {
                    if (!CelebrateWinComponent.ENABLE_TICKUP) {
                        text.text = Services.currency.format(targetValue);
                    }
                })
                .onUpdate(() => {
                    if (CelebrateWinComponent.ENABLE_TICKUP) {
                        text.text = Services.currency.format(tickObj.value);
                    }
                })
                .onComplete(() => {
                    text.text = Services.currency.format(tickObj.value);

                    Services.sound.event(SoundEvent.tickup_anywin_end);
                    if (completeSound) {
                        Services.sound.event(completeSound);
                    }

                    const winRangeKey: string = this.getWinRangeForWin(targetValue);
                    if (winRangeKey !== "") {
                        Services.sound.event(SoundEvent.tickup_win_range, winRangeKey);
                    }

                    resolve(null);
                })
                .start();
        });
    }

    private getLineWinCountupDuration() {
        let totalTime = CelebrateWinComponent.LINE_WAIT_TIME;

        if (CelebrateWinComponent.LINE_COUNTUP_TIME < Infinity) {
            totalTime += CelebrateWinComponent.LINE_COUNTUP_TIME;
        }

        return totalTime;
    }

    private getTotalWinCountupDuration() {
        let totalTime = CelebrateWinComponent.TOTAL_WAIT_TIME;

        if (CelebrateWinComponent.TOTAL_COUNTUP_TIME < Infinity) {
            totalTime += CelebrateWinComponent.TOTAL_COUNTUP_TIME;
        }

        return totalTime;
    }

    private completeTotalWin() {
        if (CelebrateWinComponent.ENABLE_PERSISTENT_TOTAL_WIN) {
            return Contract.empty();
        } else {
            return Layers.CelebrationContent.defaultScene();
        }
    }

    private getWinRatio(win: number): number {
        return win / betModel.getTotalStake();
    }

    private getWinRangeForRatio(ratio: number): string {
        for (const rangeKey of Array.from(this.WIN_RANGES.keys())) {
            const rangeObj: { from: number, to: number } = this.WIN_RANGES.get(rangeKey);
            if (ratio >= rangeObj.from) {
                if (ratio <= rangeObj.to) {
                    return rangeKey;
                }
            }
        }
        return "";
    }

    private getWinRangeForWin(win: number): string {
        const ratio = this.getWinRatio(win);
        return this.getWinRangeForRatio(ratio);
    }
}

class Win {
    public scene: Scene;
    public result: SymbolWinResult;
    public valid: boolean = true;
    public winAmount: number;
    public contract: Contract<void>;

    constructor(winAmount: number, public scenes: string[], result?: SymbolWinResult) {
        if (winAmount <= 0) {
            this.valid = false;
            return;
        }

        this.winAmount = winAmount;

        this.scene = Layers.CelebrationContent.getFirstValidScene(scenes);

        if (result) {
            this.result = result;
        }
    }

    public isBelowWinThreshold(): boolean {
        return this.winAmount <= 0 || this.winAmount / betModel.getTotalStake() < CelebrateWinComponent.TOTAL_WIN_THRESHOLD;
    }

    public canTickUp() {
        return !this.isBelowWinThreshold() && this.scene;
    }

    public playSound() {
        if (this.result) {
            PlaySymbolSoundCommand(this.result.symbolType, this.result.matches.toString(), "1");
        } else {
            Services.sound.event(SoundEvent.tickup_totalwin_start);
        }
    }

    public destroy() {
        this.contract = null;
        this.result = null;
        this.scene = null;
    }
}
